<template>
  <div>
    <v-data-table
      v-if="headers && stats && count"
      :headers="headers"
      :items="stats"
      :page="page"
      :items-per-page="perPage"
      hide-default-footer
      hide-default-header
      class="search-ranking-periodic-table"
    >
      <template v-slot:header="{ props }">
        <th
          v-for="(header, index) in props.headers"
          :key="index"
          :class="{ 'datagrid__header--sortable': header.sortable }"
          class="datagrid__header"
        >
          <div>
            <div
              class="datagrid__header-col"
              :class="{ 'datagrid__header-col--main': !header.sortable }"
              @click="header.sortable ? sortByTable(header) : () => {}"
            >
              <div>
                <div v-if="header.text" class="datagrid__rating">
                  <span>{{ header.text }}</span>
                </div>
              </div>
              <div v-if="header.sortable" class="datagrid__sort-btn">
                <v-icon
                  v-bind="filterIconAttrs(sortColumn, header.value)"
                  class="datagrid__sort-icon"
                  >icon-arrow-thin-up</v-icon
                >
                <v-icon
                  v-bind="
                    filterIconAttrs(
                      {
                        sortDesc: !sortColumn.sortDesc,
                        value: sortColumn.value
                      },
                      header.value
                    )
                  "
                  class="datagrid__sort-icon datagrid__sort-icon--down"
                  >icon-arrow-thin-up</v-icon
                >
              </div>
            </div>
          </div>
        </th>
      </template>

      <template #body.prepend="{ headers }">
        <tr>
          <td class="summary-name-col">
            <div>
              <p class="item-name">
                {{ $t("message.analytics.periodic.table.summary") }}
              </p>
              <small
                v-if="summaryStats && isGroupedMode"
                class="item-description"
              >
                {{
                  $t(
                    getFacilitiesCountLabel(summaryStats.group.facilitiesCount)
                  )
                }}
              </small>
              <small v-else class="item-description">
                {{
                  $t("message.analytics.periodic.table.summarySecondaryText")
                }}
              </small>

              <div class="summary-loader" v-if="!summaryStats">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="15"
                  width="2"
                />
              </div>
            </div>
          </td>
          <template v-if="summaryStats">
            <template v-for="(header, index) in headers">
              <td v-if="index > 0" :key="header.value" class="text-center">
                {{ summaryStats[header.value] }}
              </td>
            </template>
          </template>
          <template v-else>
            <td :colspan="keywords.length"></td>
          </template>
        </tr>
      </template>

      <template #item.gmbAddress="{ item: { gmbAddress } }">
        <div>
          <p class="item-name">{{ gmbAddress[0] }}</p>
          <small class="item-description"
            >{{ gmbAddress[1] }}
            <template v-if="gmbAddress[2]"
              >| {{ $t("message.common.storeCode") }}:
              {{ gmbAddress[2] }}</template
            >
          </small>
        </div>
      </template>
      <template #item.group="{ item: { group } }">
        <div>
          <p class="item-name">{{ group.name }}</p>
          <small class="item-description" v-if="group">{{
            $t(getFacilitiesCountLabel(group.facilitiesCount))
          }}</small>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import cuid from "cuid";
import { pluralCount } from "@/helpers/helpers";

import {
  PERIODIC_REPORT_TYPE,
  TOP_CHART_SERIE
} from "@/enums/charts-type.enum";
import { FACILITIES_FILTER_SEARCH_TYPES } from "@/enums/facilty-details-status.enum";

export default {
  name: "PeriodicTable",
  props: {
    keywords: {
      type: Array,
      required: true
    },
    page: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    },
    tableData: {
      type: Array,
      required: true
    },
    tableSummary: {
      type: Object
    },
    isGroupedData: {
      type: Boolean,
      required: true
    },
    dataType: {
      type: String,
      validator: val =>
        Object.values(FACILITIES_FILTER_SEARCH_TYPES).includes(val)
    },
    count: {
      type: Number,
      required: true
    },

    reportType: {
      type: Number,
      validator: val => Object.values(PERIODIC_REPORT_TYPE).includes(val)
    }
  },

  data() {
    return {
      headers: null,
      stats: null,
      summaryStats: null,
      sortColumn: {
        sortDesc: null,
        value: null
      },
      sortBy: false
    };
  },

  computed: {
    isTopReport() {
      return this.reportType === PERIODIC_REPORT_TYPE.TOP;
    },
    isAverageReport() {
      return this.reportType === PERIODIC_REPORT_TYPE.AVERAGE;
    },
    isGroupedMode() {
      return (
        this.isGroupedData &&
        this.dataType === FACILITIES_FILTER_SEARCH_TYPES.FACILITIES_GROUPS
      );
    }
  },

  watch: {
    tableSummary: {
      handler(value) {
        this.summaryStats = value
          ? this.mapStats([this.tableSummary])[0]
          : null;
      },
      deep: true
    }
  },

  methods: {
    filterIconAttrs(sortColumn, value) {
      if (sortColumn.sortDesc === null) {
        return [];
      }
      return {
        class: [
          {
            "datagrid__sort-icon--active":
              sortColumn.value == value && sortColumn.sortDesc === true,
            "datagrid__sort-icon--in-active":
              sortColumn.sortDesc === false && sortColumn.value == value
          }
        ]
      };
    },
    sortByTable(column) {
      let orderDirection;
      const nextColumnSortClicked =
        this.sortColumn.value && this.sortColumn.value !== column.value;

      if (nextColumnSortClicked) {
        this.sortColumn.sortDesc = true;
        this.sortColumn.value = column.value;
        orderDirection = "asc";
      } else if (this.sortColumn.sortDesc === false) {
        this.sortColumn.value = null;
        orderDirection = this.sortColumn.sortDesc = null;
      } else {
        this.sortColumn.sortDesc = !this.sortColumn.sortDesc;
        this.sortColumn.value = column.value;
        orderDirection = !this.sortColumn.sortDesc ? "desc" : "asc";
      }
      this.$emit("sort", {
        keywordId: this.sortColumn.value,
        orderDirection
      });
    },
    renderTable() {
      this.stats = [];

      this.setHeaders();
      this.setStats();
    },
    setHeaders() {
      const commonProps = {
        class: "header-cell",
        width: "118px",
        divider: true,
        sortable: true,
        align: "center"
      };
      this.headers = [
        {
          text: this.$t(
            `message.analytics.periodic.table.${
              this.isGroupedMode ? "group" : "gmbAddress"
            }`
          ),
          value: this.isGroupedMode ? "group" : "gmbAddress",
          divider: commonProps.divider,
          class: "header-cell header-cell--sticky",
          sortable: false,
          align: "start"
        }
      ];

      this.addCustomHeaders(commonProps);
    },

    addCustomHeaders(commonProps) {
      if (this.isTopReport) {
        Object.values(TOP_CHART_SERIE).forEach(serie => {
          this.headers.push({
            text: this.$t(`message.topChartSeries.${serie}`),
            value: serie,
            sortable: true,
            sortDesc: null,
            sortBy: null,
            ...commonProps
          });
        });
      } else if (this.isAverageReport) {
        if (this.keywords && Array.isArray(this.keywords)) {
          this.keywords.forEach(({ keyword }) =>
            this.headers.push({
              text: keyword.name,
              value: `${keyword.id}`,
              sortDesc: null,
              sortBy: null,
              sortable: true,
              ...commonProps
            })
          );
        }
      }
    },

    setStats() {
      if (!this.tableData) return;

      this.stats = this.mapStats(this.tableData);
    },

    mapStats(data) {
      return data.map(({ gmb_address, facility_group, results }) => {
        let stats = {
          id: cuid()
        };

        if (this.isGroupedMode) {
          stats.group = {
            name: facility_group.name,
            facilitiesCount: facility_group.facilities_count
          };
        } else {
          stats.gmbAddress = [
            gmb_address.name,
            gmb_address.formatted_address,
            gmb_address.store_code
          ];
        }

        if (this.isTopReport) {
          if (Object.values(TOP_CHART_SERIE).every(serie => serie in results)) {
            stats = { ...stats, ...results };
          }
        } else if (this.isAverageReport) {
          if (results && Array.isArray(results)) {
            results.forEach(result => {
              stats[`${result.keyword}`] =
                result.value === null ? "-" : result.value;
            });
          }
        }
        return stats;
      });
    },

    getFacilitiesCountLabel(value) {
      return pluralCount("message.pluralForms.facility", value);
    }
  }
};
</script>

<style lang="scss">
.datagrid {
  &__header-col {
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    @include font-size(12px);
    padding: $spacing * 3 $spacing * 0.5;
    cursor: pointer;

    &--main {
      justify-content: flex-start;
      cursor: auto;
    }
  }
}

.datagrid__sort-btn {
  margin-left: $spacing;
  display: flex;
  color: $gray-2;

  .datagrid__sort-icon {
    @include font-size(16px);
    &--down {
      transform: rotate(180deg);
    }
    &--active {
      color: $primary;
    }

    &--in-active {
      color: $gray-2;
    }
  }
}
.search-ranking-periodic-table {
  height: 100%;

  &.theme--light.v-data-table {
    thead tr th,
    tbody tr td:not(.v-data-table__mobile-row) {
      border-right: 0;
      border-bottom: 1px solid $table-border-color !important;
    }

    tbody tr td:not(.v-data-table__mobile-row):not(:first-child),
    thead tr:last-child th:not(:first-child) {
      border-bottom: 1px solid $table-border-color;
      position: relative;

      &::before {
        content: "";
        position: absolute;
        right: 0;
        top: 8px;
        bottom: 8px;
        width: 1px;
        background: $table-border-color;
      }

      span {
        display: inline;
      }
    }

    // UNCOMMENT WHEN SORTING SUPPORT PROVIDED
    // thead tr:last-child th:not(:first-child)::before {
    //   top: 0;
    //   bottom: 16px;
    // }
  }

  &.v-data-table {
    table-layout: fixed;
  }

  .header-cell {
    @include font-size(12px);
    font-family: $font-family-base;
    font-weight: 400;
    color: $grey-darker;
    line-height: 15px;
    height: 50px;
    padding: $spacing * 1.25;
    text-align: center;

    .v-rating--dense .v-icon {
      padding: 0;
    }

    &:first-of-type {
      padding-left: $spacing * 2;
    }
  }

  &.v-data-table {
    th,
    td {
      @include font-size(18px);
      font-weight: 400;
      color: $grey-darker;

      &:first-child {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: #fff;

        > div {
          min-width: 300px;
        }
      }

      &:not(:first-child) {
        min-width: 100px;
      }
    }

    tbody tr:hover,
    tbody tr:hover td {
      background-color: $light-4;
    }

    tbody tr:first-child td {
      background-color: $primary-light;
      font-weight: 500;

      .item-name {
        font-weight: 600;
      }

      .item-description {
        font-weight: 500;
      }
    }
  }

  &.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    height: 75px !important;
  }

  .item-name {
    @include font-size(14px);
    color: $text;
    line-height: 12px;
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  .item-description {
    display: inline-block;
    @include font-size(12px);
    color: $text;
    line-height: 1.16;
    font-weight: 400;
    opacity: 0.8;
    margin-top: $spacing * 0.5;
  }
}

.summary-name-col {
  position: relative;
}

.summary-loader {
  position: absolute;
  right: $spacing;
  top: 0;
  bottom: 0;
  width: 24px;
  display: flex;
  align-items: center;
}
</style>
